import React, { useEffect, Suspense, lazy } from 'react';
import { IntlProvider } from 'react-intl';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import langManager from 'services/translate/lang-manager';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import ModalRoot from './Modals/ModalRoot';
import Header from './Header/Header';
import Footer from './Footer/Footer';
import AccountRouteChecker from './AccountRouteChecker/AccountRouteChecker';
import { getChampionToken } from '../services/championService';
import { getChampionOwnerDataAction, getChampionPoolTilesAction } from '../redux/actions/championActions';
import { getAppConfigAction, getCoinConverterAction } from '../redux/actions/generalActions';
import PortfolioRoutes from './Pages/Portfolio/PortfolioRoutes';
import ScrollToTop from './ScrollToTop/ScrollToTop';
import IsChampionChecker from './hoc/IsChampionChecker/IsChampionChecker';
import { ETH_SYMBOL, HORD_SYMBOL } from '../constants/general';
import { getAssetBalanceAction } from '../redux/actions/assetsActions';
import { getWhitelistedPools } from '../redux/actions/createHPoolActions';
import PasswordPromt from './Common/PasswordPromt/PasswordPromt';

const defaultRichTextElements = {
  b: (chunks) => <b>{chunks}</b>,
  br: () => <br />,
  span: (chunks) => <span>{chunks}</span>,
  article: (chunks) => <article>{chunks}</article>,
};

const LandingLazy = lazy(() => import('./Pages/Landing/Landing'));
const ChampionRoutesLazy = lazy(() => import('./Pages/Champion/ChampionRoutes'));
const ChampionVerifyLazy = lazy(() => import('./Pages/Champion/ChampionVerify/ChampionVerify'));
export const CreateHPoolRoutesLazy = lazy(() => import('./Pages/CreateHPool/CreateHPoolRoutes'));
const HPoolRouteLazy = lazy(() => import('./Pages/HPool/HPoolContainer/HPoolContainer'));

const Page404Lazy = lazy(() => import('./Pages/404/Page404'));

const RoutesWrapper = ({
  getChampionOwnerDataAction,
  getChampionPoolTilesAction,
  getAppConfigAction,
  getCoinConverterAction,
  getAssetBalanceAction,
  account,
  getWhitelistedPools,
  createHPool,
}) => {
  const { is_vpool: isVpool = false } = createHPool?.hpool || {};
  useEffect(() => {
    getAppConfigAction(isVpool);
  }, [createHPool?.hpool?.is_vpool]);

  useEffect(() => {
    getCoinConverterAction('ethUsd');
    getCoinConverterAction('hordUsd', 'hord', 'usd');
    getCoinConverterAction('bnbUsd', 'binancecoin', 'usd');
  }, []);

  const refreshBalances = () => {
    getAssetBalanceAction(HORD_SYMBOL);
    getAssetBalanceAction(ETH_SYMBOL);
  };

  useEffect(() => {
    if (!account) return;
    refreshBalances();
    getWhitelistedPools(account);
  }, [account]);

  useEffect(() => {
    const championToken = getChampionToken();

    if (championToken) {
      getChampionOwnerDataAction(championToken);
      // getChampionPoolTilesAction();
    }
  }, [getChampionOwnerDataAction, getChampionPoolTilesAction]);

  return (
    <IntlProvider
      key="en"
      locale="en"
      messages={langManager.messages}
      defaultRichTextElements={defaultRichTextElements}
      onError={() => {}}
    >
      <BrowserRouter>
        <div className="app">
          <PasswordPromt />
          <Header />
          <div className="app-content-wrapper">
            <Suspense fallback={<div className="loader-between-pages" />}>
              <ScrollToTop>
                <Switch>
                  <AccountRouteChecker path="/" exact component={LandingLazy} />
                  <AccountRouteChecker path="/verify/:token" exact component={ChampionVerifyLazy} />
                  <AccountRouteChecker path="/champion" component={ChampionRoutesLazy} />

                  <IsChampionChecker
                    path="/create-pool"
                    component={CreateHPoolRoutesLazy}
                    requireLogin
                    requireVerifiedChampion
                  />
                  <AccountRouteChecker path="/:poolType/:id" component={HPoolRouteLazy} />

                  <Route path="/portfolio" component={PortfolioRoutes} />

                  <AccountRouteChecker path="/404" component={Page404Lazy} />
                  <AccountRouteChecker path="*" component={Page404Lazy} />
                </Switch>
              </ScrollToTop>
            </Suspense>
          </div>

          <Footer />
          <Suspense fallback={<div className="loader-between-pages" />}>
            <ModalRoot />
          </Suspense>
        </div>
      </BrowserRouter>
    </IntlProvider>
  );
};

RoutesWrapper.propTypes = {
  account: PropTypes.string.isRequired,
  getChampionOwnerDataAction: PropTypes.func.isRequired,
  getChampionPoolTilesAction: PropTypes.func.isRequired,
  getAppConfigAction: PropTypes.func.isRequired,
  createHPool: PropTypes.object.isRequired,
  getCoinConverterAction: PropTypes.func.isRequired,
  getAssetBalanceAction: PropTypes.func.isRequired,
  getWhitelistedPools: PropTypes.func.isRequired,
};
const mapDispatchToProps = {
  getChampionOwnerDataAction,
  getChampionPoolTilesAction,
  getAppConfigAction,
  getCoinConverterAction,
  getAssetBalanceAction,
  getWhitelistedPools,
};
const mapStateToProps = ({ wallet, createHPool }) => ({
  account: wallet.account,
  createHPool,
});

export default connect(mapStateToProps, mapDispatchToProps)(RoutesWrapper);
